import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";
import { Text } from "#components/atoms/Typographies/Text";
import { Modal } from "#components/molecules/Modal/Modal";
import { lineRemovalImpactQuery } from "#modules/optimizations/infra/lineRemovalImpact.query";
import { preapproveOptimizationInvalidation } from "#modules/optimizations/infra/optimizationInvalidationConfirmation";
import { ImpactedOptimizationList } from "#modules/optimizations/view/ImpactedOptimizationList";
import { Flex } from "#shared/view/components/Flex";

type Props = {
  opened: boolean;
  lineIds: string[];
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
};

export const DeleteQuoteRequestLineConfirmationModal = ({
  opened,
  lineIds,
  onClose,
  onConfirm,
  loading,
}: Props) => {
  const { t } = useTranslation();
  const { data: impactedOptimizations = [] } = useQuery(lineRemovalImpactQuery(lineIds, opened));
  const hasImpact = impactedOptimizations.length > 0;

  const confirm = () => {
    preapproveOptimizationInvalidation(impactedOptimizations.map(({ id }) => id));
    onConfirm();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("quoteRequestV2.deleteLine.title", { count: lineIds.length })}
      closeText={t("common.cancel")}
      primaryText={t("quoteRequestV2.deleteLine.confirm")}
      primaryVariant="destructive"
      primaryLoading={loading}
      onPrimary={confirm}
    >
      <Text size="small" color={colors.text.secondaryDefault}>
        {t("quoteRequestV2.deleteLine.body")}
      </Text>
      {hasImpact && (
        <Flex pt={spacing[3]}>
          <ImpactedOptimizationList optimizations={impactedOptimizations} withWarningHeader />
        </Flex>
      )}
    </Modal>
  );
};
